import React, { useRef } from 'react';
import { isExternal } from '../../utils/UrlUtils';
import { AlmeMarkdownProcessor } from '@alme/markdown-ts/AlmeMarkdownProcessor';
import { Icon } from '../AlmeUtilities/Icons/StyledIcon';
import { SydneyAvatar } from '../AlmeUtilities/Icons/Avatar';
import { UserAvatar } from '../AlmeUtilities/Icons/UserAvatar';
import { AgentCardContainer, AgentCardMessageContainer, AgentCardBubbleContent, UserCardContainer, AgentText, UserText, SectionHeader, } from './AlmeResponse.styles';
import { AgentCardBubble, UserCardBubble } from './ChatBubble';
import AlmeSurvey, { getSurveyResponseAction } from '../AlmeSurvey/AlmeSurvey';
import AlmeInlineForm from '../AlmeInlineForm/AlmeInlineForm';
import Link from 'syf-component-library/ui/atoms/Link';
import { getInlineFormResponseAction } from '../AlmeInlineForm/InlineFormResponseAction';
import OverlayForm from '../AlmeOverlayForm/OverlayForm';
import { getAltAutoNavResponse } from './AlternateLoginAutoNav';
import OneClickLink from '../Custom/OneClickLink';
import { createResourceUrl } from '../../utils/resourceUtils';
import ResponseFocusHandler from './ResponseFocusHandler';
import { onEnterKeyPress } from '../AlmeUtilities/dom-utils';
import { useAltLoginNavigationHook } from '../../hooks/useAltLoginNavigationHook';
import { isOverlayResponse } from '../AlmeOverlayForm/DisplayFormResponseAction';
import { isValidLiveChatOperatorResponse } from '../LiveChat/AlmeLiveChatResponse/AlmeLiveChatOperatorResponse/AlmeLiveChatOperatorResponse';
/**
 * @description Checks for navigation type and renders link with corresponding properties.
 * 'MainPage' navType utilizes the isExternal function, which strips url addresses to reveal if the address
 * is outside of the current domain (see utils/UrlUtils.ts). If the urladdress is external, the link will contain
 * the OpenInNewIcon.
 * {@linkcode isExternal}
 */
const paintUrlLink = (appConfig, navType, urlAddress, displayText, buttonProps, onLinkClick) => {
    let icon = null;
    if (isExternal(urlAddress)) {
        icon = React.createElement(Icon, { size: 20, iconType: "ExternalLinkIcon" });
    }
    const link = { TargetType: 'Url', Metadata: { UrlAddress: urlAddress, NavigationType: navType } };
    switch (navType) {
        case 'MainPage':
            return (React.createElement(Link, Object.assign({}, buttonProps, { "aria-disabled": "true", to: "", onKeyPress: ((evt) => onEnterKeyPress(evt, () => onLinkClick(link, appConfig))), onClick: () => { onLinkClick(link, appConfig); } }),
                icon,
                displayText));
        case 'None':
            return (React.createElement(Link, Object.assign({}, buttonProps, { to: "" }), displayText));
        case 'Popup':
        default:
            return (React.createElement(Link, Object.assign({}, buttonProps, { "aria-disabled": "true", to: "", onKeyPress: ((evt) => onEnterKeyPress(evt, () => onLinkClick(link, appConfig))), onClick: () => { onLinkClick(link, appConfig); } }), displayText));
    }
};
/**
 * Handle alme navUrl clicked or auto nav to navigate the url in a new tab or the current window.
 * If clicked this should use the INavUrl.LinkNavigationType else use the INavUrl.AutoNavigationType.
 * @see https://confluence.nextit.com/pages/viewpage.action?pageId=35688489
 * @see https://confluence.nextit.com/display/AlmeTech/Processing+the+Agent+Response+to+a+Conversation+Request
 */
export function onTriggerAutoNavUrl(link, appConfig, onLinkClick) {
    const urlNavUrl = link;
    // Auto Nav is not best practice and may be removed in future versions of Alme.
    // See  https://www.w3.org/TR/WCAG20/#context-changedef    
    const navlink = { TargetType: 'Url', Metadata: { UrlAddress: urlNavUrl.UrlAddress, NavigationType: urlNavUrl.AutoNavigationType } };
    onLinkClick(navlink, appConfig);
}
/**
 * @description Returns the Alme DisplayLink as a material-ui Button JSX element.
 * @see https://material-ui.com/components/buttons/
 * @returns Alme DisplayLink as a material-ui Button
 */
function paintDisplayLink(entryId, isLastResponse, onLinkClick, sectionIndex, _appConfig, focusProps, isLinkDisabled) {
    function paintLink(link, linkIndex) {
        const urlLink = link;
        const buttonProps = Object.assign({ key: `${entryId}-link-${sectionIndex}-${linkIndex}`, role: `button` }, focusProps);
        switch (link.TargetType) {
            case 'Url':
                return paintUrlLink(_appConfig, urlLink.Metadata.NavigationType, urlLink.Metadata.UrlAddress, urlLink.DisplayText, buttonProps, onLinkClick);
            case 'None':
                return (React.createElement(Link, Object.assign({}, buttonProps, { to: "" }), link.DisplayText));
            case 'Input':
                return React.createElement(OneClickLink, Object.assign({}, buttonProps, { isLinkEnabled: !isLastResponse, onClick: () => { onLinkClick(link, _appConfig); }, linkText: link.DisplayText }));
            case 'Unit':
            default:
                return React.createElement(OneClickLink, Object.assign({}, buttonProps, { isLinkEnabled: isLinkDisabled, onClick: () => { onLinkClick(link, _appConfig); }, linkText: link.DisplayText }));
        }
    }
    return paintLink;
}
const displayLinkCollection = (displayLinks, entryId, onLinkClick, appConfig, isLastResponse, focusProps, isLinkDisabled) => {
    return displayLinks && !!displayLinks.Sections.length
        ? displayLinks.Sections.reduce((elements, section) => {
            return elements.concat(paintSection(entryId, section, onLinkClick, appConfig, isLastResponse, focusProps, isLinkDisabled));
        }, [])
        : null;
};
const paintSection = (entryId, section, onLinkClick, appConfig, isLastResponse, focusProps, isLinkDisabled) => {
    return section.Links.map(paintDisplayLink(entryId, isLastResponse, onLinkClick, section.SectionID, appConfig, focusProps, isLinkDisabled));
};
/**
 * @description Returns the Alme NavUrl as a material-ui Button JSX Element.
 * This function also triggers onTriggerNavUrl with clicked=false to support auto navigation.
 * @see https://material-ui.com/components/buttons/
 * @see INavUrl
 * @returns Alme NavUrl as a material-ui Button
 */
export function paintNavUrl(entryId, navUrl, _appConfig, onLinkClick, isLastResponse, focusProps) {
    if (navUrl && navUrl.DisplayText && (navUrl.UrlAddress || navUrl.LinkNavigationType === 'None')) {
        if (navUrl.AutoNavigationType && navUrl.AutoNavigationType !== 'None' && isLastResponse) {
            onTriggerAutoNavUrl(navUrl, _appConfig, onLinkClick);
        }
        const buttonProps = Object.assign({ key: `${entryId}-url-${navUrl.UrlUID}-link-0`, role: `button` }, focusProps);
        return paintUrlLink(_appConfig, navUrl.LinkNavigationType, navUrl.UrlAddress, navUrl.DisplayText, buttonProps, onLinkClick);
    }
    else {
        return null;
    }
}
/**
 * @description Returns a h4 JSX Element that includes the text from first section HeaderText (i.e. section[0].HeaderText).
 * In this example we will only support the first section header text and not be creating multiple link sections.
 * @see IDisplayLinkSection
 * @returns Section header JSX Element
 */
const paintSectionHeader = (sections) => {
    if (sections && sections.length && sections[0].HeaderText && sections[0].Links && sections[0].Links.length) {
        return (React.createElement(SectionHeader, null, sections[0].HeaderText));
    }
    else {
        return null;
    }
};
/**
 * Returns the users masked input from the Alme response or null if there is no masked input.
 * @see IAlmeResponse
 * @returns Users masked input or null
 */
export function getMaskedUserInput(response) {
    return response.maskedInput && typeof response.maskedInput === 'string' ? response.maskedInput.trim() : null;
}
export function getAgentResponse(response) {
    const sharedProps = {
        className: `alme--window--conversation--response--alme__message__text`,
    };
    if (response.html || isValidLiveChatOperatorResponse(response)) {
        return React.createElement(AgentText, Object.assign({}, sharedProps, { dangerouslySetInnerHTML: { __html: response.html || response.text } }));
    }
    const responseText = typeof response.text === 'string' ? response.text.trim() : response.text;
    return response.text ? React.createElement(AgentText, Object.assign({}, sharedProps), responseText) : null;
}
export function getAgentResponseText(response) {
    return typeof response.text === 'string' ? response.text.trim() : undefined;
}
function renderInlineForm(inlineFormResponseAction, responseRevision, isLastResponse, onSubmitFormAppEvent, focusProps, fromHistory) {
    return inlineFormResponseAction ?
        React.createElement(AlmeInlineForm, { responseAction: inlineFormResponseAction, responseRevision: responseRevision, disabled: !isLastResponse, onSubmitFormAppEvent: onSubmitFormAppEvent, tabIndex: focusProps === null || focusProps === void 0 ? void 0 : focusProps.tabIndex, fromHistory: fromHistory })
        : null;
}
const paintSectionHeaderResponse = (displayLinks) => {
    return displayLinks && !!displayLinks.Sections.length ? paintSectionHeader(displayLinks.Sections) : null;
};
const AlmeResponse = (props) => {
    const response = AlmeMarkdownProcessor.ConvertAlmeResponse(props.response);
    const responseRevision = String(props.responseId);
    const entryId = response.extensionData.entryId || responseRevision;
    const userResponseRef = useRef(null);
    const agentResponseRef = useRef(null);
    const maskedUserInput = getMaskedUserInput(response);
    const inlineSurveyDialogResponseAction = getSurveyResponseAction(response);
    const agentResponse = getAgentResponse(response);
    const inlineFormResponseAction = getInlineFormResponseAction(response);
    const altLoginAutoNavResponse = getAltAutoNavResponse(response);
    const isOverlayForm = isOverlayResponse(response);
    useAltLoginNavigationHook(altLoginAutoNavResponse, props.response.fromHistory, props.isLastResponse, props.appConfig);
    const isLinkDisabled = props.response.isLinkDisabled || props.isLiveChatConnected;
    return (React.createElement(React.Fragment, null,
        React.createElement(ResponseFocusHandler, { responseId: props.responseId, isLastResponse: props.isLastResponse, isInlineForm: !!inlineFormResponseAction, fromHistory: !!props.response.fromHistory, isOverlayForm: isOverlayForm, disableResponseCard: !!props.response.didSessionEndAsNotEngaged },
            maskedUserInput &&
                React.createElement(UserCardContainer, { ref: userResponseRef, id: `alme-user-card-${responseRevision}` },
                    React.createElement(UserAvatar, { size: 40 }),
                    React.createElement(UserCardBubble, { key: `${entryId}-user` },
                        React.createElement(UserText, null, maskedUserInput))),
            agentResponse
                ?
                    React.createElement(AgentCardContainer, { ref: agentResponseRef, id: `alme-iva-card-${responseRevision}` },
                        React.createElement(SydneyAvatar, { size: 40, iconUrl: createResourceUrl(props.appConfig.webUrl, "Sydney.png") }),
                        React.createElement(AgentCardMessageContainer, null,
                            React.createElement(AgentCardBubble, null,
                                React.createElement(AgentCardBubbleContent, null,
                                    agentResponse,
                                    paintNavUrl(entryId, response.navUrl, props.appConfig, props.onDisplayLinkClick, props.isLastResponse, props.focusProps),
                                    renderInlineForm(inlineFormResponseAction, responseRevision, props.isLastResponse, props.onSubmitFormAppEvent, props.focusProps, props.response.fromHistory),
                                    paintSectionHeaderResponse(response.displayLinkCollection),
                                    displayLinkCollection(response.displayLinkCollection, entryId, props.onDisplayLinkClick, props.appConfig, props.isLastResponse, props.focusProps, isLinkDisabled))),
                            inlineSurveyDialogResponseAction &&
                                React.createElement(AlmeSurvey, { entryId: entryId, setIsDialogOpen: props.setIsDialogOpen, responseAction: inlineSurveyDialogResponseAction, onSendFormAppEvent: props.onSubmitFormAppEvent, isLastResponse: props.isLastResponse, focusProps: props.focusProps, fromHistory: props.response.fromHistory })))
                : null),
        React.createElement(OverlayForm, { appConfig: props.appConfig, response: response, isLastResponse: props.isLastResponse, onSubmitFormAppEvent: props.onSubmitFormAppEvent })));
};
export default AlmeResponse;
